import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { ShareOptions } from 'components/Modals';
import { SocialButtonsText } from 'components/PostComponent/PostComponent.style';
import { useOutsideClick } from 'hooks/useOutsideClick';
import {
  LikeIcon,
  LikeOutlinedIcon,
  MessageIcon,
  ShareIcon,
} from 'public/assets/icons';
import { anonymousClickHandler } from 'utils/anonymousClickHandler';
import { likeVehicle, unlikeVehicle } from 'services';
import { likePost, unlikePost } from 'services/likePost';
import {
  actionTypes,
  openSidebarComments,
  openSidebarCreateEvent,
  openSideBarCreateLinkPost,
  openSideBarCreatePhotoPost,
  openSidebarChatConversation,
  openSidebarAttendEvent,
  openSidebarLikes,
  openSidebarInvolvedUsers,
} from 'store/sideBarControler/actions';
import { ISocialActivityAction } from './ISocialActivityAction';

import {
  OutlinedLikeIconWrap,
  SocialActivityActionWrapper,
  SocialButtonContainer,
  SocialButtonsContainer,
} from './SocialActivityAction.styles';
import { addClubInfoToUrl } from 'utils/helpers';
import { useSelector } from 'react-redux';

const SocialActivityAction = ({
  isLike = false,
  likes = 0,
  variant,
  id,
  origin,
  originId,
  type,
  isEventLikes = false,
  commentsCount,
  comments,
  shares,
  sharesCount,
}: ISocialActivityAction) => {
  const activeClub = useSelector((state) => state?.club?.activeClub);
  const [isShareMenuOpen, setIsShareMenuOpen] = useState<boolean>(false);
  const [isLikes, setIsLikes] = useState(isLike);
  const [likeCount, setLikeCount] = useState(likes);

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const refShareModal = useRef(null);

  useOutsideClick(refShareModal, () => {
    setIsShareMenuOpen(false);
  });

  const handleLikeClick = () => {
    setIsLikes((prev) => !prev);
    if (isLikes) {
      if (variant === 'car') {
        unlikeVehicle(id);
      } else {
        unlikePost(id);
      }
      setLikeCount((prev) => prev - 1);
    } else {
      if (variant === 'car') {
        likeVehicle(id);
      } else {
        likePost(id);
      }
      setLikeCount((prev) => prev + 1);
    }
  };

  const updateLikeCount = useCallback(() => {
    setLikeCount(likes);
  }, [likes]);

  const updateIsLikeCount = useCallback(() => {
    setIsLikes(isLike);
  }, [isLike]);

  const onCommentsClick = (postId, postType) => {
    if (variant === 'car') {
      dispatch(
        openSidebarComments({
          actionType: actionTypes.OPEN_SIDEBAR_COMMENTS,
          commentsPostId: id,
          commentsType: 'car',
        })
      );
      return;
    }
    dispatch(
      openSidebarComments({
        actionType: actionTypes.OPEN_SIDEBAR_COMMENTS,
        commentsPostId: postId,
        commentsType: postType,
      })
    );
  };

  const getPostUrl = () => {
    const { origin: URLOrigin } = window.location;
    let url = URLOrigin;

    switch (type) {
      case 1:
      case 3:
        url = `${url}/p/${id}`;
        url = addClubInfoToUrl(url, activeClub);
        break;
      case 4:
        url = `${url}/v/${originId}`;
        url = addClubInfoToUrl(url, activeClub);
        break;
      case 16:
        break;
      case 6:
        url = `${url}/e/${originId}`;        
        url = addClubInfoToUrl(url, activeClub);
        break;
      case 7:
        url = `${url}/g/${originId}`;
        url = addClubInfoToUrl(url, activeClub);
        break;
      default:
        break;
    }

    return url;
  };

  useEffect(() => {
    updateLikeCount();
  }, [updateLikeCount]);

  useEffect(() => {
    updateIsLikeCount();
  }, [updateIsLikeCount]);

  return (
    <SocialActivityActionWrapper>
      <SocialButtonsContainer>
        <SocialButtonContainer
          onClick={() => anonymousClickHandler(() => handleLikeClick())}
        >
          <LikeIcon isLike={isLikes} />
          <SocialButtonsText ml={4}>
            {t('common:likesScreenHeader')}
          </SocialButtonsText>
        </SocialButtonContainer>

        <SocialButtonContainer
          onClick={() =>
            anonymousClickHandler(() => onCommentsClick(id, origin))
          }
          ml={24}
        >
          <MessageIcon />
          <SocialButtonsText ml={4}>
            {isEventLikes ? commentsCount : comments}
          </SocialButtonsText>
        </SocialButtonContainer>
        <SocialButtonContainer
          onClick={() =>
            anonymousClickHandler(() => setIsShareMenuOpen(!isShareMenuOpen))
          }
          ref={refShareModal}
          ml={24}
        >
          <ShareIcon />
          <SocialButtonsText ml={4}>
            {isEventLikes ? sharesCount : shares}
          </SocialButtonsText>

          {isShareMenuOpen && (
            <ShareOptions
              onCancelClick={() => setIsShareMenuOpen(false)}
              url={getPostUrl()}
              type={type}
              postId={type === 4 ? originId || id : id}
            />
          )}
        </SocialButtonContainer>
      </SocialButtonsContainer>
      {likeCount > 0 && (
        <SocialButtonContainer
          onClick={() =>
            dispatch(
              openSidebarLikes({
                postId: type === 1 || type === 3 ? id : originId || id,
                postType: type,
              })
            )
          }
        >
          <OutlinedLikeIconWrap>
            <LikeOutlinedIcon />
          </OutlinedLikeIconWrap>

          <SocialButtonsText ml={6}>{likeCount}</SocialButtonsText>
        </SocialButtonContainer>
      )}
    </SocialActivityActionWrapper>
  );
};

export default SocialActivityAction;
